// UPLOAD-NEVER-BLOCKS (founder 2026-10-30: "the modal took 6.9s to close"): the Upload modal hands
// its files to this queue and closes at once. The queue sends them in the background — a few at a
// time, with real byte progress on the live API (XHR upload events; the in-memory API answers at
// once) — and the upload tray and the Docs list show each file "Uploading… n%" until its bytes are
// stored, then "Processing…" until its text has been read. While bytes are still going out, closing
// the tab asks first (beforeunload); nothing else ever waits on an upload.
import { PAGE_UPLOAD_LIMITS } from "@companyos/schema/contracts";
import { ApiError, newIdempotencyKey } from "@companyos/sdk";
import { useSyncExternalStore } from "react";
import type { Api } from "../../api/client";
import type { PageDetail } from "./page-dialogs";

export type UploadJob = {
  key: string;
  companyId: string;
  slug: string;
  name: string;
  size: number;
  /** Bytes sent so far (the whole size once stored). */
  loaded: number;
  state: "queued" | "uploading" | "stored" | "failed";
  error?: string | undefined;
  /** Once stored: the doc made from it. */
  page?: { id: string; title: string };
  /** A sharing step after the upload failed (the doc exists, unshared). */
  unshared?: string;
};

type Request = {
  api: Api;
  companyId: string;
  slug: string;
  file: File;
  /** The multipart fields besides `file` (kind, read_only, shares). */
  fields: Record<string, string>;
  /** After the doc exists (people and DEs to share with); a failure is reported, not retried. */
  after?: (page: PageDetail) => Promise<void>;
  onStored?: (page: PageDetail) => void;
};

let jobs: UploadJob[] = [];
const listeners = new Set<() => void>();
const waiting: (() => Promise<void>)[] = [];
let running = 0;
const PARALLEL = 3;

function publish(next: UploadJob[]) {
  jobs = next;
  for (const l of listeners) l();
  guardUnload();
}

const patch = (key: string, p: Partial<UploadJob>) =>
  publish(jobs.map((j) => (j.key === key ? { ...j, ...p } : j)));

/** Removes finished jobs (or one). */
export function dismissUploads(key?: string) {
  const keep = jobs.filter((j) =>
    key ? j.key !== key : j.state === "queued" || j.state === "uploading",
  );
  for (const j of jobs) if (!keep.includes(j)) requests.delete(j.key);
  publish(keep);
}

/**
 * UPLOAD-STUCK-FIX: the percent the tray shows. Bytes handed to the network are not a stored file:
 * 100% only once the server has answered that it stored it.
 */
export const uploadPercent = (j: Pick<UploadJob, "state" | "loaded" | "size">) =>
  j.state === "stored" ? 100 : j.size ? Math.min(99, Math.floor((j.loaded / j.size) * 100)) : 0;

export const inFlight = (list: readonly UploadJob[]) =>
  list.some((j) => j.state === "queued" || j.state === "uploading");

const onBeforeUnload = (e: BeforeUnloadEvent) => {
  e.preventDefault();
  // Older browsers need a value to show their own "Leave site?" prompt.
  e.returnValue = "";
};
let guarded = false;
function guardUnload() {
  if (typeof window === "undefined") return;
  const want = inFlight(jobs);
  if (want === guarded) return;
  guarded = want;
  if (want) window.addEventListener("beforeunload", onBeforeUnload);
  else window.removeEventListener("beforeunload", onBeforeUnload);
}

export function useUploadJobs(): readonly UploadJob[] {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => jobs,
    () => jobs,
  );
}

const problemOf = (e: unknown, fallback: string) =>
  e instanceof ApiError
    ? (e.problem?.detail ?? e.problem?.title ?? fallback)
    : e instanceof Error && e.message
      ? e.message
      : fallback;

/**
 * UPLOAD-STUCK-FIX (founder 2026-10-31: a 17 MB PDF sat at "Uploading… 90%" for good — a proxy
 * cut the body and no answer ever came): an upload never waits forever. No bytes going out for
 * `STALL_MS`, or no answer `ANSWER_MS` after the last byte (the server answers as soon as the file
 * is stored), ends it as failed with Retry.
 */
export const STALL_MS = 45_000;
export const ANSWER_MS = 120_000;

/** Why an upload's HTTP answer failed, in a few words. */
export function uploadFailure(status: number, body: unknown): string {
  const p = body as { detail?: string; title?: string } | null;
  if (status === 413)
    return p?.detail && !/^body exceeds/.test(p.detail)
      ? p.detail
      : `File too large: up to ${PAGE_UPLOAD_LIMITS.maxMb} MB`;
  if (p?.detail || p?.title) return (p.detail ?? p.title) as string;
  if (status >= 500) return `The server failed (${status})`;
  return `Refused (${status})`;
}

/** `POST pages/upload` with byte progress (XHR on the live API; the SDK on the in-memory one). */
function send(
  r: Request,
  form: FormData,
  key: string,
  onProgress: (loaded: number) => void,
): Promise<PageDetail> {
  if (r.api.mode !== "http" || !r.api.baseUrl)
    return r.api.client.pages.upload({
      params: { co: r.companyId },
      idempotencyKey: key,
      body: form as never,
    });
  const url = `${r.api.baseUrl.replace(/\/$/, "")}/v1/companies/${r.companyId}/pages/upload`;
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let lastMove = Date.now();
    let sent = false;
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearInterval(watch);
      fn();
    };
    const fail = (why: string) => finish(() => reject(new Error(why)));
    // the watchdog: bytes stopped going out, or no answer after the last one
    const watch = setInterval(() => {
      const quiet = Date.now() - lastMove;
      if (!sent && quiet > STALL_MS) {
        xhr.abort();
        fail("The upload stalled: no data went out for 45 s");
      } else if (sent && quiet > ANSWER_MS) {
        xhr.abort();
        fail("No answer from the server after the file was sent");
      }
    }, 1_000);
    xhr.open("POST", url);
    xhr.withCredentials = true;
    xhr.setRequestHeader("accept", "application/json, application/problem+json");
    xhr.setRequestHeader("idempotency-key", key);
    xhr.upload.onprogress = (e) => {
      lastMove = Date.now();
      if (e.lengthComputable) onProgress(e.loaded);
    };
    xhr.upload.onload = () => {
      lastMove = Date.now();
      sent = true;
    };
    xhr.onerror = () => fail("The connection dropped");
    xhr.ontimeout = () => fail("The upload timed out");
    xhr.onabort = () => fail("The upload was stopped");
    xhr.onload = () => {
      let body: unknown = null;
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        body = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) finish(() => resolve(body as PageDetail));
      else
        finish(() =>
          reject(
            new ApiError("pages.upload", xhr.status, {
              ...((body as object | null) ?? {}),
              detail: uploadFailure(xhr.status, body),
            } as never),
          ),
        );
    };
    xhr.send(form);
  });
}

/** What each job was asked to do, so Retry can send it again. */
const requests = new Map<string, Request>();

/** UPLOAD-STUCK-FIX: sends a failed upload again (the same file and choices). */
export function retryUploadJob(key: string): void {
  const r = requests.get(key);
  const job = jobs.find((j) => j.key === key);
  if (!r || !job || job.state !== "failed") return;
  patch(key, { state: "queued", loaded: 0, error: undefined });
  waiting.push(() => run(r, key));
  pump();
}

async function run(r: Request, key: string) {
  patch(key, { state: "uploading" });
  try {
    const form = new FormData();
    form.append("file", r.file, r.file.name);
    for (const [k, v] of Object.entries(r.fields)) form.append(k, v);
    const page = await send(r, form, newIdempotencyKey(), (loaded) =>
      patch(key, { loaded: Math.min(loaded, r.file.size) }),
    );
    requests.delete(key);
    patch(key, {
      state: "stored",
      loaded: r.file.size,
      page: { id: page.id, title: page.title },
    });
    r.onStored?.(page);
    if (r.after)
      await r.after(page).catch((e) => patch(key, { unshared: problemOf(e, "not shared") }));
  } catch (e) {
    patch(key, { state: "failed", error: problemOf(e, "could not be uploaded") });
  }
}

function pump() {
  while (running < PARALLEL && waiting.length) {
    const next = waiting.shift();
    if (!next) break;
    running += 1;
    void next().finally(() => {
      running -= 1;
      pump();
    });
  }
}

/** Queues files to upload in the background; returns at once. */
export function enqueueUploads(list: readonly Request[]): void {
  const made = list.map((r) => ({
    r,
    job: {
      key: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
      companyId: r.companyId,
      slug: r.slug,
      name: r.file.name,
      size: r.file.size,
      loaded: 0,
      state: "queued" as const,
    },
  }));
  publish([...jobs, ...made.map((m) => m.job)]);
  for (const m of made) {
    requests.set(m.job.key, m.r);
    waiting.push(() => run(m.r, m.job.key));
  }
  pump();
}
